/**
 * Promo Emails Card Component
 * Promotional emails checkbox with Rumios bonus
 */

import { Gift, Sparkles, Loader2 } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { PROMO_EMAIL_RUMIOS_MULTIPLIER } from '../utils/constants';

interface PromoEmailsCardProps {
  enabled: boolean;
  isLoading: boolean;
  onToggle: (enabled: boolean) => void;
}

export function PromoEmailsCard({
  enabled,
  isLoading,
  onToggle,
}: PromoEmailsCardProps) {
  const { t } = useLanguage();

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Gift className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">
              {t('notifications.promo.title', 'Promotional Emails')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('notifications.promo.subtitle', 'Stay updated with special deals and offers')}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* Checkbox row */}
        <label className="flex items-start gap-4 cursor-pointer group">
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={enabled}
              onChange={e => onToggle(e.target.checked)}
              disabled={isLoading}
              className="sr-only"
            />
            <div
              className={`w-5 h-5 border-2 rounded transition-colors ${
                enabled
                  ? 'bg-primary-600 border-primary-600'
                  : 'border-gray-300 group-hover:border-primary-400'
              } ${isLoading ? 'opacity-50' : ''}`}
            >
              {enabled && !isLoading && (
                <svg
                  className="w-full h-full text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
              {isLoading && (
                <Loader2 className="w-full h-full p-0.5 text-white animate-spin" />
              )}
            </div>
          </div>
          <div>
            <div className="font-medium text-gray-900">
              {t('notifications.promo.checkbox', 'Receive promotional emails')}
            </div>
            <div className="text-sm text-gray-500">
              {t(
                'notifications.promo.description',
                'Get notified about exclusive deals, new features, and special offers'
              )}
            </div>
          </div>
        </label>

        {/* Rumios Bonus Banner */}
        <div
          className={`p-4 rounded-xl transition-all ${
            enabled
              ? 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200'
              : 'bg-gray-50 border border-gray-200'
          }`}
        >
          <div className="flex items-start gap-3">
            <div
              className={`p-2 rounded-lg ${
                enabled ? 'bg-amber-100' : 'bg-gray-200'
              }`}
            >
              <Sparkles
                className={`w-5 h-5 ${
                  enabled ? 'text-amber-600' : 'text-gray-400'
                }`}
              />
            </div>
            <div className="flex-1">
              <div
                className={`font-semibold ${
                  enabled ? 'text-amber-800' : 'text-gray-600'
                }`}
              >
                {enabled
                  ? t('notifications.promo.bonusActive', "You're earning bonus Rumios!")
                  : t(
                      'notifications.promo.bonusInactive',
                      `Subscribe to earn ${PROMO_EMAIL_RUMIOS_MULTIPLIER}x Rumios`
                    )}
              </div>
              <div
                className={`text-sm mt-1 ${
                  enabled ? 'text-amber-700' : 'text-gray-500'
                }`}
              >
                {enabled
                  ? t(
                      'notifications.promo.bonusActiveDesc',
                      `Your daily Rumios are multiplied by ${PROMO_EMAIL_RUMIOS_MULTIPLIER}x!`
                    )
                  : t(
                      'notifications.promo.bonusInactiveDesc',
                      'Enable promotional emails to boost your daily Rumios rewards'
                    )}
              </div>
            </div>
            {enabled && (
              <div className="flex-shrink-0">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-sm">
                  {PROMO_EMAIL_RUMIOS_MULTIPLIER}x
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
